<template>
    <div class="container d-flex justify-content-center align-items-center" :style="{height: '85vh'}">
        <SpinnerLoading v-if="confirming" id="fullscreen-loading"/>
        <div v-if="processConfirm" class="action">
            <h2 class="h2-style mb-5 align-self-center">{{$t("confirmAcc")}}</h2>
            <a href="/" class="btn btn-primary w-50 align-self-center border-0">{{$t("homePage")}}</a>
        </div>
        <!-- Account created by an administrator: it has no password yet, so the confirmation link
             (which only carries the one-time token) asks for one here. -->
        <form v-else-if="needsPassword" @submit.prevent="setPasswordClicked" class="col-12 form-group my-2">
            <h2 class="h2-style text-center">{{$t("login.setPasswordTitle")}}</h2>
            <div class="row py-3">
                <div class="col-12 text-center">
                    <span>{{ $t("login.setPasswordText") }}</span>
                </div>
            </div>
            <div class="col-12 form-group my-2">
                <label for="password">{{ $t("login.pass") }}</label>
                <input type="password" id="password" name="password" v-model="password" autocomplete="new-password" class="form-control" :class="{ 'is-invalid': submitted && hasPasswordError }"/>
                <div v-if="submitted && hasPasswordError" class="invalid-feedback">
                    <span v-if="!$v.password.required">{{ $t("login.passVal") }}</span>
                    <span v-if="errors.Password">{{errors.Password}}</span>
                    <span v-if="errors.PasswordTooShort">{{errors.PasswordTooShort}}</span>
                    <span v-if="errors.PasswordRequiresNonAlphanumeric">{{errors.PasswordRequiresNonAlphanumeric}}</span>
                    <span v-if="errors.PasswordRequiresDigit">{{errors.PasswordRequiresDigit}}</span>
                    <span v-if="errors.PasswordRequiresLower">{{errors.PasswordRequiresLower}}</span>
                    <span v-if="errors.PasswordRequiresUpper">{{errors.PasswordRequiresUpper}}</span>
                </div>
            </div>
            <div class="col-12 form-group my-2">
                <label for="confirmPassword">{{ $t("login.rpass") }}</label>
                <input type="password" v-model="confirmPassword" id="confirmPassword" name="confirmPassword" autocomplete="new-password" class="form-control" :class="{ 'is-invalid': submitted && $v.confirmPassword.$error }" />
                <div v-if="submitted && $v.confirmPassword.$error" class="invalid-feedback">
                    <span v-if="!$v.confirmPassword.required">{{ $t("login.valPass") }}</span>
                    <span v-else-if="!$v.confirmPassword.sameAsPassword">{{ $t("login.matchPass") }}</span>
                </div>
            </div>
            <div class="col-12 py-3 text-center">
                <button type="submit" class="btn btn-secondary" v-bind:class="{disabled: confirming}" :style="{minWidth: '160px'}">
                    <span v-show="!confirming">{{$t("login.resetBtn")}}</span>
                    <span v-show="confirming" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <span v-show="confirming" class="sr-only">{{$t("loading")}}</span>
                </button>
            </div>
        </form>
        <form v-else-if="!confirming" @submit.prevent="resendEmailClicked" class="col-12 form-group my-2">
            <h2 class="h2-style text-center">{{$t("login.resendConfirmEmail")}}</h2>
            <div class="row py-3">
                <div class="col-12 text-center">
                    <span>{{ $t("login.description") }}</span>
                </div>
            </div>
            <label for="email">{{ $t("login.email") }}</label>
            <input type="text" id="email" name="email" v-model="email" class="form-control" :class="{ 'is-invalid': submitted && hasEmailError }"/>
            <div v-if="submitted && hasEmailError" class="invalid-feedback">
                <span v-if="!$v.email.required">{{ $t("login.userVal") }}</span>
                <span v-if="!$v.email.email">{{ $t("login.invEmail") }}</span>
                <span v-if="errors.Username">{{errors.Username}}</span>
                <span v-if="errors.Email">{{errors.Email}}</span>
                <span v-if="errors.DuplicateEmail">{{errors.DuplicateEmail}}</span>
            </div>
            <div class="col-12 mt-5 mb-5 text-center">
                <button type="submit" class="btn btn-secondary" v-bind:class="{disabled: loading}" :style="{minWidth: '140px'}">
                    <span v-show="!loading">{{$t("login.resend")}}</span>
                    <span v-show="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <span v-show="loading" class="sr-only">{{$t("loading")}}</span>
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { API_HELPER } from "@/helpers/api.js";
import { UTILS_HELPER } from "@/helpers/utils.js"
import { required, email, sameAs } from "vuelidate/lib/validators";
import SpinnerLoading from "@/components/SpinnerLoading.vue";

export default {
    components: {
        SpinnerLoading,
    },
    metaInfo () {
        return {
            title: `${"/ConfirmAccountTitle"} - FactoryPlay`,
            htmlAttrs: {
                lang: this.$i18n.locale,
            },
            link: [
                {rel: 'alternate', hreflang: 'en', href: API_HELPER.pathJoin([process.env.VUE_APP_FRONT, this.$t("vueRouter.confirmAccount", 'en')])},
                {rel: 'alternate', hreflang: 'pt', href: API_HELPER.pathJoin([process.env.VUE_APP_FRONT, this.$t("vueRouter.confirmAccount", 'pt')])},
                {rel: 'alternate', hreflang: 'es', href: API_HELPER.pathJoin([process.env.VUE_APP_FRONT, this.$t("vueRouter.confirmAccount", 'es')])},
            ]
        }
    },
    data: function () {
        return {
            username: "",
            token: "",
            // Set-password form (accounts created without a password)
            needsPassword: false,
            password: "",
            confirmPassword: "",
            confirming: false,
            loading: false,
            //resend
            email: null,
            submitted: false,
        }
    },
    validations() {
        return this.needsPassword
            ? { password: { required }, confirmPassword: { required, sameAsPassword: sameAs('password') }, email: {} }
            : { email: { required, email }, password: {}, confirmPassword: {} };
    },
    created: function (){
        // The link only carries the username and the one-time confirmation token. Never read a
        // password from the URL.
        this.username = this.$route.query.username;
        this.token = this.$route.query.token;
        if (this.username && this.token)
            this.confirmationAccount(null);
    },
    methods: {
        ...mapActions('account', ['confirmAccount']),
        ...mapActions('account', ['requestToken']),
        ...mapActions({
            clearAlert: 'alert/clear'
        }),
        confirmationAccount(password) {
            this.clearAlert();
            this.confirming = true;
            const { username, token } = this;
            this.confirmAccount({ password, username, token })
        },
        setPasswordClicked: function()
        {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.password.$invalid || this.$v.confirmPassword.$invalid) {
                return;
            }
            this.submitted = false;
            this.confirmationAccount(this.password);
        },
        resendEmailClicked: function()
        {
            this.submitted = true;

            // stop here if form is invalid
            this.$v.$touch();

            if (this.$v.email.$invalid) {
                return;
            }

            this.loading = true;
            const { email } = this;
            const username = email;
            this.requestToken(username);

        },
    },
    computed: {
        ...mapState({
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        ...mapState('account', ['processConfirm']),
        ...mapState('account', ['processTask']),

        errors: function()
        {
            if (!this.alertOutput) return {};
            return this.alertOutput.Errors || this.alertOutput.errors || {};
        },
        hasEmailError()
        {
            return this.$v[UTILS_HELPER.convertToCamelCase("Email")].$error || Object.prototype.hasOwnProperty.call(this.errors, "Username") || Object.prototype.hasOwnProperty.call(this.errors, "InvalidEmail") || Object.prototype.hasOwnProperty.call(this.errors, "DuplicateEmail");
        },
        hasPasswordError()
        {
            return this.$v.password.$error || Object.keys(this.errors).some(k => k.startsWith("Password") && k !== "PasswordRequired");
        }
    },
    watch: {
        processConfirm(val)
        {
            if (val === null) return;   // request in flight
            this.confirming = false;
            if (val) { this.needsPassword = false; return; }

            if (this.username && this.token)
            {
                if (Object.prototype.hasOwnProperty.call(this.errors, "PasswordRequired"))
                {
                    // Token accepted, the account just needs its first password.
                    this.needsPassword = true;
                    return;
                }
                if (this.needsPassword)
                {
                    // Password rejected by the API's policy: keep the form, show the field errors.
                    this.submitted = true;
                    return;
                }
                this.$fire({
                    title: this.$t("login.resendConfirmTitle"),
                    text: this.$t("login.resendConfirmTextError"),
                    type: "error"
                })
            }
        },
        processTask(val)
        {
            if (val === null) return;
            this.loading = false;
            if(val)
            {
                this.$fire({
                    title: this.$t("login.resendConfirmTitle"),
                    text: this.$t("login.resendConfirmTextSuccess"),
                    type: "success"
                })
                this.$router.push({name: 'Login'});
            }
        }
    }
}
</script>

<style scoped>
.container{
    max-width: 500px!important;
}
>>>.label-style{
    color: #17224d;
}
.confirmAcc-style{
    color: #17224d;
    margin-top: 5rem;
}
.submit-button
{
    background-color: #17224d;
    border: 4px solid white;
    color: white;
    text-transform: uppercase;
    font-weight: bold;
}
.action
{
    justify-content: center;
    align-content: center;
    display: flex;
    flex-direction: column;
}
.h2-style{
    color: #17224d;
}
</style>
